import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Phone, Building2, Trash2, CalendarClock, MapPin, ChevronDown, ChevronRight, Settings2, GripVertical, History, FileText, Flame, Paperclip, Download, Trophy, XCircle, Pencil, Calendar, Clock, FileSpreadsheet, Eye } from "lucide-react";
import { toast } from "sonner";
import { API, backendUrl, inr, imgUrl, fmtDate, fmtDateTime, downloadExcel } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";

const SOURCES = ["IndiaMart", "TradeIndia", "Alibaba", "Website", "Referral", "Walk-in", "Other"];
const ACT_TYPES = ["call", "meeting", "followup", "note"];
const PAGE_SIZE = 10;
const CCODES = ["+91", "+1", "+44", "+971", "+65", "+86", "+880", "+977"];
const CC_DIGITS = { "+91": 10, "+1": 10, "+44": 10, "+971": 9, "+65": 8, "+86": 11, "+880": 10, "+977": 10 };

const splitPhone = (p) => {
  const clean = (p || "").replace(/[^\d+]/g, "");
  for (const c of CCODES) if (clean.startsWith(c)) return { ccode: c, phone: clean.slice(c.length) };
  return { ccode: "+91", phone: clean.replace(/^\+/, "").replace(/^0+/, "") };
};

const fmtTime12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return t;
  const ap = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
};

const TimePicker12 = ({ value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const [h24, mm] = value ? value.split(":").map(Number) : [null, null];
  const hour12 = h24 === null ? null : h24 % 12 === 0 ? 12 : h24 % 12;
  const ampm = h24 === null ? "AM" : h24 >= 12 ? "PM" : "AM";
  const apply = (hh, mn, ap) => {
    if (hh === null || mn === null) return;
    let h = hh % 12;
    if (ap === "PM") h += 12;
    onChange(`${String(h).padStart(2, "0")}:${String(mn).padStart(2, "0")}`);
  };
  const sel = "bg-indigo-600 text-white";
  const btn = "rounded-md px-2 py-1 text-sm transition-colors duration-100 hover:bg-indigo-50";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs transition-colors duration-150 hover:bg-slate-50" data-testid={testid}>
          <Clock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className={value ? "text-slate-900" : "text-slate-400"}>{value ? fmtTime12(value) : "hh:mm AM"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start" data-testid={`${testid}-popover`}>
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Hour</div>
            <div className="grid grid-cols-6 gap-1">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((hh) => (
                <button key={hh} type="button" onClick={() => apply(hh, mm ?? 0, ampm)} className={`${btn} ${hour12 === hh ? sel : "text-slate-700"}`} data-testid={`${testid}-hour-${hh}`}>{hh}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Minute</div>
            <div className="grid grid-cols-6 gap-1">
              {Array.from({ length: 12 }, (_, i) => i * 5).map((mn) => (
                <button key={mn} type="button" onClick={() => apply(hour12 ?? 12, mn, ampm)} className={`${btn} ${mm === mn ? sel : "text-slate-700"}`} data-testid={`${testid}-min-${mn}`}>{String(mn).padStart(2, "0")}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-1">
              {["AM", "PM"].map((ap) => (
                <button key={ap} type="button" onClick={() => apply(hour12 ?? 12, mm ?? 0, ap)} className={`${btn} border ${ampm === ap ? "border-indigo-600 " + sel : "border-slate-200 text-slate-700"}`} data-testid={`${testid}-${ap.toLowerCase()}`}>{ap}</button>
              ))}
            </div>
            <div className="flex gap-1">
              {value && <button type="button" onClick={() => { onChange(""); setOpen(false); }} className="text-xs text-slate-400 hover:text-rose-500" data-testid={`${testid}-clear`}>Clear</button>}
              <Button size="sm" className="h-7 bg-indigo-600 px-2.5 text-xs hover:bg-indigo-700" onClick={() => setOpen(false)} data-testid={`${testid}-done`}>Done</Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const DatePicker = ({ value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selected = value ? new Date(value + "T00:00:00") : undefined;
  const toYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs transition-colors duration-150 hover:bg-slate-50" data-testid={testid}>
          <Calendar className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className={value ? "text-slate-900" : "text-slate-400"}>{value ? fmtDate(value) : "Pick date"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={`${testid}-popover`}>
        <CalendarPicker
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => { if (d) { onChange(toYmd(d)); setOpen(false); } }}
          disabled={{ before: today }}
        />
      </PopoverContent>
    </Popover>
  );
};

const emptyLead = { name: "", company: "", city: "", ccode: "+91", phone: "", email: "", gstin: "", source: "IndiaMart", value: 0, assigned_to: "" };
const emptyAct = { type: "call", title: "", due_date: "", due_time: "", priority: "medium" };
const emptyCust = { name: "", company: "", phone: "", email: "", address: "", gstin: "", source: "" };

const attKind = (a) => {
  const ct = a.content_type || "";
  const nm = (a.name || "").toLowerCase();
  if (ct.startsWith("image/")) return "image";
  if (ct === "application/pdf" || nm.endsWith(".pdf")) return "pdf";
  return "other";
};

export default function Leads() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [myPipeline, setMyPipeline] = useState(false);
  const [folded, setFolded] = useState({});
  const [shown, setShown] = useState({});
  const [stageOpen, setStageOpen] = useState(false);
  const [stageForm, setStageForm] = useState({ label: "", color: "#64748B" });
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [cities, setCities] = useState([]);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyLead);
  const [allCustomers, setAllCustomers] = useState([]);
  const [custMatches, setCustMatches] = useState([]);
  const [custPicked, setCustPicked] = useState(false);
  const [custEditOpen, setCustEditOpen] = useState(false);
  const [custForm, setCustForm] = useState(emptyCust);
  const [active, setActive] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [act, setAct] = useState(emptyAct);
  const [editAddress, setEditAddress] = useState("");
  const [editItems, setEditItems] = useState([]);
  const [leadDocs, setLeadDocs] = useState([]);
  const [allDocs, setAllDocs] = useState([]);
  const [docPreview, setDocPreview] = useState(null);
  const [attUploading, setAttUploading] = useState(false);
  const [attPreview, setAttPreview] = useState(null);
  const attRef = useRef(null);
  const kanbanRef = useRef(null);

  useEffect(() => {
    if (active) {
      setEditAddress(active.address || "");
      setEditItems((active.items || []).map((i) => ({ ...i })));
      API.get(`/leads/${active.id}/documents`).then((r) => setLeadDocs(r.data)).catch(() => setLeadDocs([]));
      API.get("/documents").then((r) => setAllDocs(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  const attachDoc = async (docId) => {
    try {
      await API.post(`/documents/${docId}/attach`, { lead_id: active.id });
      const { data } = await API.get(`/leads/${active.id}/documents`);
      setLeadDocs(data);
      toast.success("Document attached to lead");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Attach failed");
    }
  };

  const deleteDoc = async (id) => {
    await API.delete(`/documents/${id}`);
    setDocPreview(null);
    setAllDocs((p) => p.filter((d) => d.id !== id));
    const { data } = await API.get(`/leads/${active.id}/documents`);
    setLeadDocs(data);
    toast.success("Document deleted");
  };

  const load = useCallback(async () => {
    const { data } = await API.get("/leads", { params: { search } });
    setLeads(data);
  }, [search]);

  const loadStages = useCallback(async () => {
    const { data } = await API.get("/stages");
    setStages(data);
  }, []);

  useEffect(() => {
    load().catch(() => {});
    loadStages().catch(() => {});
    API.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    API.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    API.get("/meta/cities").then((r) => setCities(r.data)).catch(() => {});
  }, [load, loadStages]);

  useEffect(() => {
    if (createOpen) {
      setCustMatches([]);
      setCustPicked(false);
      API.get("/customers").then((r) => setAllCustomers(r.data)).catch(() => {});
    }
  }, [createOpen]);

  const userName = (id) => users.find((u) => u.id === id)?.name || "";
  const stageLabel = (id) => stages.find((s) => s.id === id)?.label || id;

  const onKanbanDragOver = (e) => {
    e.preventDefault();
    const el = kanbanRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const zone = 90;
    if (e.clientX < rect.left + zone) el.scrollLeft -= 30;
    else if (e.clientX > rect.right - zone) el.scrollLeft += 30;
  };

  const addStage = async () => {
    if (!stageForm.label.trim()) return toast.error("Stage name is required");
    try {
      await API.post("/stages", stageForm);
      setStageOpen(false);
      setStageForm({ label: "", color: "#64748B" });
      toast.success("Stage added to pipeline");
      loadStages();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add stage");
    }
  };

  const deleteStage = async (stage) => {
    if (!window.confirm(`Delete stage "${stage.label}"? This is only possible when no leads are in it.`)) return;
    try {
      await API.delete(`/stages/${stage.id}`);
      toast.success(`Stage "${stage.label}" deleted`);
      loadStages();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete stage");
    }
  };

  const visibleLeads = myPipeline ? leads.filter((l) => l.assigned_to === user?.id) : leads;

  const reorderStages = async (draggedId, targetId) => {
    if (draggedId === targetId) return;
    const ids = stages.map((s) => s.id);
    ids.splice(ids.indexOf(draggedId), 1);
    ids.splice(ids.indexOf(targetId), 0, draggedId);
    setStages(ids.map((id) => stages.find((s) => s.id === id)));
    try {
      await API.post("/stages/reorder", { order: ids });
      toast.success("Pipeline order saved");
    } catch {
      toast.error("Could not save order");
      loadStages();
    }
  };

  const pickLeadProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    const next = editItems.map((it, idx) =>
      idx === i
        ? p
          ? { ...it, product_id: pid, name: p.name, image_url: p.image_url || "", rate: p.sell_price || 0 }
          : { ...it, product_id: "", name: "", image_url: "" }
        : it
    );
    setEditItems(next);
    saveRequirements(editAddress, next);
  };

  const saveRequirements = async (addressArg, itemsArg) => {
    const address = addressArg ?? editAddress;
    const items = itemsArg ?? editItems;
    if (items.some((i) => !i.name.trim() || !(Number(i.qty) > 0) || !(Number(i.rate) > 0))) return;
    try {
      const { data } = await API.patch(`/leads/${active.id}`, {
        address,
        items: items.map((i) => ({ ...i, qty: Number(i.qty), rate: Number(i.rate), tax_pct: Number(i.tax_pct ?? 18) })),
      });
      setActive(data);
      setLeads((p) => p.map((l) => (l.id === data.id ? data : l)));
      toast.success("Saved", { duration: 1800 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const onLeadNameChange = (v) => {
    setForm((f) => ({ ...f, name: v }));
    setCustPicked(false);
    const s = v.trim().toLowerCase();
    if (s.length < 2) return setCustMatches([]);
    setCustMatches(
      allCustomers
        .filter((c) => [c.name, c.company, c.phone].some((f) => (f || "").toLowerCase().includes(s)))
        .slice(0, 6)
    );
  };

  const applyCustomer = (c) => {
    const ph = splitPhone(c.phone);
    setForm((f) => ({ ...f, name: c.name || f.name, company: c.company || "", ccode: ph.ccode, phone: ph.phone, email: c.email || "", gstin: c.gstin || "", city: c.city || f.city }));
    setCustMatches([]);
    setCustPicked(true);
    toast.success(`Autofilled from customer "${c.name}"`);
  };

  const createLead = async () => {
    if (!form.name.trim()) return toast.error("Contact name is required");
    const digits = form.phone.replace(/\D/g, "");
    const need = CC_DIGITS[form.ccode] || 0;
    if (!digits) return toast.error("Phone number is required");
    if (digits.length !== need) return toast.error(`${form.ccode} phone numbers must have exactly ${need} digits`);
    const city = form.city.trim();
    if (!city) return toast.error("City / location is required");
    if (cities.length && !cities.some((c) => c.toLowerCase() === city.toLowerCase())) {
      return toast.error("Please select a valid city from the suggestions");
    }
    const { ccode, ...rest } = form;
    const payload = { ...rest, city, phone: ccode + digits, value: Number(form.value) || 0 };
    if (!payload.assigned_to) delete payload.assigned_to;
    try {
      const { data } = await API.post("/leads", payload);
      setLeads((p) => [data, ...p]);
      setCreateOpen(false);
      setForm(emptyLead);
      setCustMatches([]);
      toast.success("Lead created — customer record saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create lead");
    }
  };

  const moveLead = async (id, stage) => {
    const { data } = await API.patch(`/leads/${id}`, { stage });
    setLeads((p) => p.map((l) => (l.id === id ? data : l)));
    if (active?.id === id) setActive(data);
    toast.success(`Moved to ${stageLabel(stage)}`);
  };

  const patchActive = async (patch) => {
    const { data } = await API.patch(`/leads/${active.id}`, patch);
    setActive(data);
    setLeads((p) => p.map((l) => (l.id === data.id ? data : l)));
  };

  const markOutcome = async (outcome) => {
    const target = stages.find((s) => s.id === outcome || (s.label || "").toLowerCase() === outcome)?.id || outcome;
    if (outcome === "won" && !active.customer_id) return convert();
    await patchActive({ stage: target });
  };

  const openCustEdit = async () => {
    if (!active?.customer_id) return toast.error("No customer linked to this lead");
    try {
      const { data } = await API.get(`/customers/${active.customer_id}`);
      setCustForm({ name: data.name || "", company: data.company || "", phone: data.phone || "", email: data.email || "", address: data.address || "", gstin: data.gstin || "", source: data.source || "" });
      setCustEditOpen(true);
    } catch {
      toast.error("Could not load customer");
    }
  };

  const saveCustEdit = async () => {
    if (!custForm.name.trim()) return toast.error("Name is required");
    try {
      await API.patch(`/customers/${active.customer_id}`, custForm);
      const { data } = await API.patch(`/leads/${active.id}`, { name: custForm.name, company: custForm.company, phone: custForm.phone, email: custForm.email, gstin: custForm.gstin });
      setActive(data);
      setLeads((p) => p.map((l) => (l.id === data.id ? data : l)));
      setCustEditOpen(false);
      toast.success("Customer details updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const deleteLead = async (id) => {
    await API.delete(`/leads/${id}`);
    setLeads((p) => p.filter((l) => l.id !== id));
    setActive(null);
    toast.success("Lead deleted");
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    const { data } = await API.post(`/leads/${active.id}/notes`, { text: noteText });
    setActive(data);
    setNoteText("");
  };

  const addActivity = async () => {
    if (!act.title.trim()) return;
    try {
      const { data } = await API.post(`/leads/${active.id}/activities`, act);
      setActive(data);
      setLeads((p) => p.map((l) => (l.id === data.id ? data : l)));
      setAct(emptyAct);
      toast.success("Activity added — task created in Tasks");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add activity");
    }
  };

  const toggleAct = async (idx, done) => {
    const { data } = await API.patch(`/leads/${active.id}/activities/${idx}`, { done });
    setActive(data);
    setLeads((p) => p.map((l) => (l.id === data.id ? data : l)));
    toast.success(done ? "Activity & task marked done" : "Activity & task reopened", { duration: 1800 });
  };

  const uploadAttachment = async (file) => {
    if (attUploading) return;
    setAttUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "Lead Attachments");
      const up = await API.post("/upload", fd);
      const { data } = await API.post(`/leads/${active.id}/attachments`, {
        name: file.name,
        url: up.data.url,
        size: up.data.size,
        content_type: up.data.content_type,
      });
      setActive(data);
      toast.success("File attached");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setAttUploading(false);
      if (attRef.current) attRef.current.value = "";
    }
  };

  const deleteAttachment = async (idx) => {
    const { data } = await API.delete(`/leads/${active.id}/attachments/${idx}`);
    setActive(data);
    setAttPreview(null);
    toast.success("Attachment removed");
  };

  const convert = async () => {
    try {
      await API.post(`/leads/${active.id}/convert`);
      const { data } = await API.get(`/leads/${active.id}`);
      setActive(data);
      setLeads((p) => p.map((l) => (l.id === data.id ? data : l)));
      toast.success("Marked as Won — converted to customer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conversion failed");
    }
  };

  const linkedQuote = leadDocs.find((d) => d.type === "quotation");
  const itemsGstTotal = (list) => list.reduce((a, i) => a + (Number(i.qty) || 0) * (Number(i.rate) || 0) * (1 + (Number(i.tax_pct ?? 18) || 0) / 100), 0);

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="sticky top-0 left-0 z-20 -mx-1 flex w-full flex-wrap items-center justify-between gap-3 bg-[#f8fafc] px-1 py-2" data-testid="leads-toolbar">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Leads Pipeline</h1>
          <p className="text-sm text-slate-500">Drag cards between stages or open a card for details</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5" data-testid="my-pipeline-toggle">
            <Switch checked={myPipeline} onCheckedChange={setMyPipeline} />
            <span className="text-sm font-medium text-slate-700">My Pipeline</span>
          </label>
          <Button variant="outline" onClick={() => setStageOpen(true)} data-testid="add-stage-button">
            <Settings2 className="mr-1 h-4 w-4" /> Add Stage
          </Button>
          <Input
            placeholder="Search name, company, phone, city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
            data-testid="lead-search-input"
          />
          <Button variant="outline" onClick={() => downloadExcel("/leads/export", "leads.xlsx")} data-testid="leads-export-button">
            <FileSpreadsheet className="mr-1 h-4 w-4" /> Excel
          </Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setCreateOpen(true)} data-testid="new-lead-button">
            <Plus className="mr-1 h-4 w-4" /> New Lead
          </Button>
        </div>
      </div>

      <div ref={kanbanRef} onDragOver={onKanbanDragOver} className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
        {stages.map((s) => {
          const items = visibleLeads.filter((l) => l.stage === s.id);
          const value = items.reduce((a, l) => a + (l.value || 0), 0);
          const isFolded = !!folded[s.id];
          const limit = shown[s.id] || PAGE_SIZE;
          const visibleItems = items.slice(0, limit);
          return (
            <div
              key={s.id}
              draggable
              onDragStart={(e) => e.dataTransfer.setData("stageDragId", s.id)}
              className={`${isFolded ? "w-44" : "w-72"} shrink-0 rounded-lg bg-slate-100/70 p-3 transition-[width] duration-200`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                const stageDrag = e.dataTransfer.getData("stageDragId");
                if (stageDrag) return reorderStages(stageDrag, s.id);
                const id = e.dataTransfer.getData("leadId");
                if (id) moveLead(id, s.id);
              }}
              data-testid={`kanban-column-${s.id}`}
            >
              <div className="mb-3 flex items-center justify-between px-1">
                <div className="flex min-w-0 items-center gap-2">
                  <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-slate-300" />
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.color }} />
                  <span className="truncate text-sm font-semibold text-slate-800">{s.label}</span>
                  <Badge variant="secondary" className="text-xs">{items.length}</Badge>
                </div>
                <div className="flex items-center gap-1">
                  {!isFolded && <span className="text-xs font-medium text-slate-500">{inr(value)}</span>}
                  {user?.role === "admin" && (
                    <button
                      onClick={() => deleteStage(s)}
                      className="rounded p-0.5 text-slate-400 transition-colors duration-150 hover:bg-rose-100 hover:text-rose-600"
                      data-testid={`delete-stage-${s.id}`}
                      aria-label="Delete stage"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => setFolded((p) => ({ ...p, [s.id]: !p[s.id] }))}
                    className="rounded p-0.5 text-slate-400 transition-colors duration-150 hover:bg-slate-200 hover:text-slate-700"
                    data-testid={`fold-stage-${s.id}`}
                    aria-label={isFolded ? "Expand stage" : "Fold stage"}
                  >
                    {isFolded ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {!isFolded && (
              <div className="space-y-2">
                {visibleItems.map((l) => (
                  <div
                    key={l.id}
                    draggable
                    onDragStart={(e) => {
                      e.stopPropagation();
                      e.dataTransfer.setData("leadId", l.id);
                    }}
                    onClick={() => setActive(l)}
                    className="cursor-pointer rounded-md border border-slate-200 bg-white p-3 shadow-sm transition-shadow duration-150 hover:shadow-md"
                    data-testid={`lead-card-${l.id}`}
                  >
                    <div className="text-sm font-semibold text-slate-900">{l.name}</div>
                    {l.company && (
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                        <Building2 className="h-3 w-3" /> {l.company}
                      </div>
                    )}
                    {l.city && (
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500" data-testid={`lead-city-${l.id}`}>
                        <MapPin className="h-3 w-3" /> {l.city}
                      </div>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-sm font-semibold text-indigo-600">{inr(l.value)}</span>
                      <Badge variant="outline" className="text-[10px]">{l.source}</Badge>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between">
                      <span className="text-[11px] text-slate-400">{l.assigned_to ? `→ ${userName(l.assigned_to)}` : ""}</span>
                      <span className="flex items-center gap-2">
                        {(l.activities || []).filter((a) => !a.done).length > 0 && (
                          <span className="flex items-center gap-1 text-[11px] font-medium text-amber-600" data-testid={`lead-activities-icon-${l.id}`}>
                            <CalendarClock className="h-3 w-3" /> {(l.activities || []).filter((a) => !a.done).length}
                          </span>
                        )}
                        {l.priority === "high" && (
                          <Flame className="h-3.5 w-3.5 text-rose-500" data-testid={`lead-priority-icon-${l.id}`} />
                        )}
                      </span>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="rounded-md border border-dashed border-slate-300 p-4 text-center text-xs text-slate-400">Drop leads here</div>}
                {items.length > limit && (
                  <button
                    onClick={() => setShown((p) => ({ ...p, [s.id]: limit + PAGE_SIZE }))}
                    className="w-full rounded-md border border-slate-200 bg-white py-1.5 text-xs font-medium text-indigo-600 transition-colors duration-150 hover:bg-indigo-50"
                    data-testid={`load-more-${s.id}`}
                  >
                    Load more ({items.length - limit} left)
                  </button>
                )}
              </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="new-lead-dialog">
          <DialogHeader>
            <DialogTitle>New Lead</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="relative col-span-2">
              <Label>Contact Name *</Label>
              <Input value={form.name} onChange={(e) => onLeadNameChange(e.target.value)} className="mt-1.5" data-testid="lead-name-input" autoComplete="off" />
              {custMatches.length > 0 && (
                <div className="absolute z-30 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid="customer-suggestions">
                  {custMatches.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => applyCustomer(c)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors duration-100 hover:bg-indigo-50"
                      data-testid={`customer-suggestion-${c.id}`}
                    >
                      <span className="font-medium text-slate-800">{c.name} {c.company ? <span className="text-slate-400">· {c.company}</span> : null}</span>
                      <span className="text-xs text-slate-400">{c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
              {form.name.trim().length >= 2 && custMatches.length === 0 && !custPicked ? (
                <p className="mt-1 text-[11px] text-slate-400" data-testid="customer-no-match-hint">No existing customer matches — a new customer record will be created with this lead.</p>
              ) : (
                <p className="mt-1 text-[11px] text-slate-400">Type 2+ letters — matching existing customers appear above; clicking one auto-fills the details.</p>
              )}
            </div>
            <div>
              <Label>Company</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5" data-testid="lead-company-input" />
            </div>
            <div>
              <Label>Phone (WhatsApp) *</Label>
              <div className="mt-1.5 flex gap-2">
                <Select value={form.ccode} onValueChange={(v) => setForm({ ...form, ccode: v })}>
                  <SelectTrigger className="w-24 shrink-0" data-testid="lead-ccode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CCODES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  inputMode="numeric"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, CC_DIGITS[form.ccode] || 15) })}
                  placeholder={`${CC_DIGITS[form.ccode] || 10} digits`}
                  data-testid="lead-phone-input"
                />
              </div>
              <p className="mt-1 text-[11px] text-slate-400">Digits only — {form.ccode} needs exactly {CC_DIGITS[form.ccode]} digits.</p>
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="lead-email-input" />
            </div>
            <div>
              <Label>City *</Label>
              <Input list="lead-city-list" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" placeholder="Type to search cities..." data-testid="lead-city-input" autoComplete="off" />
              <datalist id="lead-city-list">
                {cities.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <Label>GSTIN (optional)</Label>
              <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} placeholder="Shown on lead & customer" className="mt-1.5" data-testid="lead-gstin-input" />
            </div>
            <div>
              <Label>Expected Value (₹)</Label>
              <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1.5" data-testid="lead-value-input" />
            </div>
            <div>
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger className="mt-1.5" data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assign To</Label>
              <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="lead-assignee-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700" onClick={createLead} data-testid="create-lead-submit">Create Lead</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={stageOpen} onOpenChange={setStageOpen}>
        <DialogContent data-testid="stage-dialog">
          <DialogHeader>
            <DialogTitle>Add Pipeline Stage</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Stage Name *</Label>
              <Input value={stageForm.label} onChange={(e) => setStageForm({ ...stageForm, label: e.target.value })} placeholder="e.g. Follow Up, Negotiation" className="mt-1.5" data-testid="stage-label-input" />
            </div>
            <div>
              <Label>Color</Label>
              <Input type="color" value={stageForm.color} onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })} className="mt-1.5 h-10 w-20 p-1" data-testid="stage-color-input" />
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={addStage} data-testid="save-stage-button">Add Stage</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={custEditOpen} onOpenChange={setCustEditOpen}>
        <DialogContent data-testid="edit-customer-dialog">
          <DialogHeader>
            <DialogTitle>Edit Customer Details</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input value={custForm.name} onChange={(e) => setCustForm({ ...custForm, name: e.target.value })} className="mt-1.5" data-testid="cust-edit-name-input" />
            </div>
            <div>
              <Label>Company</Label>
              <Input value={custForm.company} onChange={(e) => setCustForm({ ...custForm, company: e.target.value })} className="mt-1.5" data-testid="cust-edit-company-input" />
            </div>
            <div>
              <Label>Phone (WhatsApp)</Label>
              <Input value={custForm.phone} onChange={(e) => setCustForm({ ...custForm, phone: e.target.value })} className="mt-1.5" data-testid="cust-edit-phone-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={custForm.email} onChange={(e) => setCustForm({ ...custForm, email: e.target.value })} className="mt-1.5" data-testid="cust-edit-email-input" />
            </div>
            <div>
              <Label>GSTIN</Label>
              <Input value={custForm.gstin} onChange={(e) => setCustForm({ ...custForm, gstin: e.target.value })} className="mt-1.5" data-testid="cust-edit-gstin-input" />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Input value={custForm.address} onChange={(e) => setCustForm({ ...custForm, address: e.target.value })} className="mt-1.5" data-testid="cust-edit-address-input" />
            </div>
          </div>
          <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700" onClick={saveCustEdit} data-testid="save-customer-edit-button">Save Changes</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!attPreview} onOpenChange={(o) => !o && setAttPreview(null)}>
        <DialogContent className="max-w-3xl" data-testid="attachment-preview-dialog">
          {attPreview && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-8 text-base">{attPreview.name}</DialogTitle>
              </DialogHeader>
              <div className="flex max-h-[65vh] items-center justify-center overflow-auto rounded-md bg-slate-50" data-testid="attachment-preview-body">
                {attKind(attPreview) === "image" ? (
                  <img src={imgUrl(attPreview.url)} alt={attPreview.name} className="max-h-[65vh] w-auto object-contain" />
                ) : attKind(attPreview) === "pdf" ? (
                  <iframe src={imgUrl(attPreview.url)} title={attPreview.name} className="h-[65vh] w-full" />
                ) : (
                  <div className="flex flex-col items-center gap-3 p-16 text-slate-500">
                    <Paperclip className="h-14 w-14 text-slate-300" />
                    <div className="text-sm">Preview not available for this file type.</div>
                    <Button variant="outline" onClick={() => window.open(imgUrl(attPreview.url), "_blank")} data-testid="attachment-download-button">
                      <Download className="mr-2 h-4 w-4" /> Download / Open
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>{attPreview.by} · {fmtDate(attPreview.at)}</span>
                <Button variant="ghost" size="sm" onClick={() => window.open(imgUrl(attPreview.url), "_blank")} data-testid="attachment-open-newtab">
                  <Download className="mr-1 h-3.5 w-3.5" /> Open in new tab
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!docPreview} onOpenChange={(o) => !o && setDocPreview(null)}>
        <DialogContent className="max-w-4xl" data-testid="doc-preview-dialog">
          {docPreview && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 pr-8 text-base">
                  {docPreview.number}
                  <Badge variant="outline" className="text-[10px] capitalize">{docPreview.type}</Badge>
                  <Badge className="bg-slate-100 text-[10px] capitalize text-slate-600 hover:bg-slate-100">{docPreview.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <iframe src={`${backendUrl}/api/documents/${docPreview.id}/pdf`} title={docPreview.number} className="h-[60vh] w-full rounded-md border border-slate-200" data-testid="doc-preview-iframe" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => navigate(`/documents/${docPreview.id}/edit`)} data-testid="doc-preview-edit">
                  <Pencil className="mr-1 h-4 w-4" /> Edit
                </Button>
                <Button variant="destructive" onClick={() => deleteDoc(docPreview.id)} data-testid="doc-preview-delete">
                  <Trash2 className="mr-1 h-4 w-4" /> Delete
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="lead-detail-sheet">
          {active && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center justify-between pr-6 font-heading text-xl">
                  {active.name}
                  {active.customer_id && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={openCustEdit} data-testid="edit-customer-button" aria-label="Edit customer details">
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-5">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4" /> {active.phone || "-"}</div>
                  <div className="text-slate-600">{active.email || "-"}</div>
                  <div className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4" /> {active.city || "-"}</div>
                  <div className="flex items-center gap-2 text-slate-600"><Building2 className="h-4 w-4" /> {active.company || "-"}</div>
                  <div className="font-semibold text-indigo-600">{inr(active.value)}</div>
                  {active.gstin && <div className="text-slate-600" data-testid="lead-gstin-display">GSTIN: {active.gstin}</div>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Stage</Label>
                    <Select value={active.stage} onValueChange={(v) => patchActive({ stage: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Assigned To</Label>
                    <Select value={active.assigned_to || "none"} onValueChange={(v) => patchActive({ assigned_to: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-1.5" data-testid="lead-detail-assignee-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={active.priority || "normal"} onValueChange={(v) => patchActive({ priority: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="lead-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High — highlighted on dashboard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-900">Address</h3>
                  <Input value={editAddress} onChange={(e) => setEditAddress(e.target.value)} onBlur={() => saveRequirements()} placeholder="Customer address" data-testid="lead-address-input" />
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900">Products &amp; Requirements</h3>
                    <Button size="sm" variant="outline" onClick={() => setEditItems((p) => [...p, { product_id: "", name: "", qty: 1, rate: 0, tax_pct: 18 }])} data-testid="add-lead-item-button">
                      <Plus className="mr-1 h-3.5 w-3.5" /> Add
                    </Button>
                  </div>
                  <div className="space-y-2" data-testid="lead-items-list">
                    {editItems.map((it, i) => (
                      <div key={i} className="flex gap-2.5 rounded-md border border-slate-200 p-2.5" data-testid={`lead-item-row-${i}`}>
                        {it.image_url ? (
                          <img src={imgUrl(it.image_url)} alt={it.name} className="h-14 w-14 shrink-0 rounded-md border border-slate-200 object-cover" data-testid={`lead-item-image-${i}`} />
                        ) : (
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-slate-100 text-[10px] text-slate-400" data-testid={`lead-item-image-${i}`}>No img</div>
                        )}
                        <div className="grid flex-1 grid-cols-2 gap-2">
                          <Select value={it.product_id || "custom"} onValueChange={(v) => pickLeadProduct(i, v === "custom" ? "" : v)}>
                            <SelectTrigger data-testid={`lead-item-product-${i}`}><SelectValue placeholder="Product *" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="custom">Custom product</SelectItem>
                              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Input placeholder="Product name *" value={it.name} onChange={(e) => setEditItems((p) => p.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} onBlur={() => saveRequirements()} data-testid={`lead-item-name-${i}`} />
                          <div className="col-span-2 grid grid-cols-3 gap-2">
                            <div>
                              <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Qty *</div>
                              <Input type="number" min="1" placeholder="0" value={it.qty} onChange={(e) => setEditItems((p) => p.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))} onBlur={() => saveRequirements()} data-testid={`lead-item-qty-${i}`} />
                            </div>
                            <div>
                              <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Rate ₹ *</div>
                              <Input type="number" min="0" placeholder="0" value={it.rate} onChange={(e) => setEditItems((p) => p.map((x, idx) => idx === i ? { ...x, rate: e.target.value } : x))} onBlur={() => saveRequirements()} data-testid={`lead-item-rate-${i}`} />
                            </div>
                            <div>
                              <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">GST %</div>
                              <Input type="number" min="0" max="28" step="0.1" placeholder="18" value={it.tax_pct ?? 18} onChange={(e) => setEditItems((p) => p.map((x, idx) => idx === i ? { ...x, tax_pct: e.target.value } : x))} onBlur={() => saveRequirements()} data-testid={`lead-item-gst-${i}`} />
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-indigo-600" title="Line total incl. GST">{inr((Number(it.qty) || 0) * (Number(it.rate) || 0) * (1 + (Number(it.tax_pct ?? 18) || 0) / 100))}</span>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { const next = editItems.filter((_, idx) => idx !== i); setEditItems(next); saveRequirements(editAddress, next); }} data-testid={`lead-item-remove-${i}`} aria-label="Remove item">
                              <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {editItems.length > 0 && (
                    <div className="mt-2 flex items-center justify-between rounded-md bg-indigo-50 px-3 py-2">
                      <span className="text-sm font-medium text-slate-700">Lead Value (incl. item-wise GST)</span>
                      <span className="font-heading text-base font-bold text-indigo-700" data-testid="lead-items-total">{inr(itemsGstTotal(editItems))}</span>
                    </div>
                  )}
                  <p className="mt-2 text-xs text-slate-400" data-testid="autosave-hint">Changes save automatically and sync with the linked quotation — both share the same data.</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => markOutcome("won")} disabled={active.stage === "won"} data-testid="mark-won-button">
                    <Trophy className="mr-1 h-4 w-4" /> {active.stage === "won" ? "Won" : "Mark Won"}
                  </Button>
                  <Button size="sm" variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50" onClick={() => markOutcome("lost")} disabled={active.stage === "lost"} data-testid="mark-lost-button">
                    <XCircle className="mr-1 h-4 w-4" /> {active.stage === "lost" ? "Lost" : "Mark Lost"}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => deleteLead(active.id)} data-testid="delete-lead-button">
                    <Trash2 className="mr-1 h-4 w-4" /> Delete
                  </Button>
                </div>

                <div>
                  <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <CalendarClock className="h-4 w-4 text-indigo-500" /> Activities
                  </h3>
                  <div className="space-y-2">
                    {(active.activities || []).map((a, i) => (
                      <div key={i} className="flex items-start gap-2 rounded-md border border-slate-200 p-2.5">
                        <Checkbox checked={a.done} onCheckedChange={(v) => toggleAct(i, !!v)} data-testid={`activity-checkbox-${i}`} />
                        <div className={a.done ? "opacity-50 line-through" : ""}>
                          <div className="flex items-center gap-2 text-sm text-slate-800">
                            {a.title}
                            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold capitalize ${a.priority === "high" ? "bg-rose-100 text-rose-700" : a.priority === "low" ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-700"}`}>{a.priority || "medium"}</span>
                          </div>
                          <div className="text-xs capitalize text-slate-500">{a.type} · due {a.due_date || "-"}{a.due_time ? ` ${fmtTime12(a.due_time)}` : ""} · {a.by} · {fmtDateTime(a.at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Select value={act.type} onValueChange={(v) => setAct({ ...act, type: v })}>
                      <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ACT_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={act.priority} onValueChange={(v) => setAct({ ...act, priority: v })}>
                      <SelectTrigger data-testid="activity-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low priority</SelectItem>
                        <SelectItem value="medium">Medium priority</SelectItem>
                        <SelectItem value="high">High priority</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input placeholder="Activity title" value={act.title} onChange={(e) => setAct({ ...act, title: e.target.value })} data-testid="activity-title-input" />
                    <div className="grid grid-cols-2 gap-2">
                      <DatePicker value={act.due_date} onChange={(v) => setAct({ ...act, due_date: v })} testid="activity-date-input" />
                      <TimePicker12 value={act.due_time} onChange={(v) => setAct({ ...act, due_time: v })} testid="activity-time-input" />
                    </div>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-400">Adding an activity auto-creates a task in the Tasks board — ticking it done also completes the task (and vice-versa).</p>
                  <Button size="sm" variant="outline" className="mt-2" onClick={addActivity} data-testid="add-activity-button">Add Activity</Button>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <FileText className="h-4 w-4 text-indigo-500" /> Quotations &amp; Invoices
                    </h3>
                    <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-700" onClick={() => linkedQuote ? navigate(`/documents/${linkedQuote.id}/edit`) : navigate(`/documents/new?type=quotation&lead_id=${active.id}`)} data-testid="new-quotation-from-lead-button">
                      {linkedQuote ? (<><Pencil className="mr-1 h-3.5 w-3.5" /> Edit {linkedQuote.number}</>) : (<><Plus className="mr-1 h-3.5 w-3.5" /> New Quotation</>)}
                    </Button>
                  </div>
                  <div className="space-y-2" data-testid="lead-documents-list">
                    {leadDocs.map((d) => (
                      <div key={d.id} className="rounded-md border border-slate-200 p-2.5" data-testid={`lead-doc-${d.id}`}>
                        <div className="flex items-center justify-between">
                          <button onClick={() => setDocPreview(d)} className="flex min-w-0 items-center gap-2 text-left" data-testid={`lead-doc-preview-${d.id}`}>
                            <span className="text-sm font-semibold text-indigo-700 hover:underline">{d.number}</span>
                            <Badge variant="outline" className="text-[10px] capitalize">{d.type}</Badge>
                            <Badge className="bg-slate-100 text-[10px] capitalize text-slate-600 hover:bg-slate-100">{d.status}</Badge>
                            <Badge className={`text-[10px] ${d.attach_manual ? "bg-amber-100 text-amber-700 hover:bg-amber-100" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"}`} data-testid={`lead-doc-origin-${d.id}`}>{d.attach_manual ? "Manual" : "Auto"}</Badge>
                          </button>
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-semibold">{inr(d.total)}</span>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDocPreview(d)} data-testid={`lead-doc-view-${d.id}`} aria-label="Preview document">
                              <Eye className="h-3.5 w-3.5 text-indigo-500" />
                            </Button>
                          </div>
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`lead-doc-items-${d.id}`}>
                          {(d.items || []).slice(0, 3).map((it, j) => (
                            <span key={j} className="flex items-center gap-1 rounded bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-600">
                              {it.image_url && <img src={imgUrl(it.image_url)} alt="" className="h-4 w-4 rounded object-cover" />}
                              {it.name} ×{it.qty}
                            </span>
                          ))}
                          {(d.items || []).length > 3 && <span className="text-[10px] text-slate-400">+{d.items.length - 3} more</span>}
                        </div>
                      </div>
                    ))}
                    {leadDocs.length === 0 && <div className="text-xs text-slate-400">No documents linked yet.</div>}
                  </div>
                  <Select onValueChange={attachDoc}>
                    <SelectTrigger className="mt-2 border-indigo-300 text-indigo-700" data-testid="attach-doc-select"><SelectValue placeholder="Attach quotation / invoice..." /></SelectTrigger>
                    <SelectContent>
                      {allDocs.filter((d) => (!d.lead_id || d.lead_id === active.id) && !leadDocs.some((ld) => ld.id === d.id)).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.number} · {d.customer?.name || "-"} · {inr(d.total)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-[11px] text-slate-400">One document links to one lead only — already-linked documents don't appear here.</p>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <Paperclip className="h-4 w-4 text-indigo-500" /> Attachments
                    </h3>
                    <input ref={attRef} type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadAttachment(e.target.files[0])} data-testid="lead-attachment-input" />
                    <Button size="sm" variant="outline" onClick={() => attRef.current?.click()} disabled={attUploading} data-testid="add-attachment-button">
                      <Paperclip className="mr-1 h-3.5 w-3.5" /> {attUploading ? "Uploading..." : "Attach File"}
                    </Button>
                  </div>
                  <div className="space-y-1.5" data-testid="lead-attachments-list">
                    {(active.attachments || []).map((a, i) => (
                      <div key={i} className="flex items-center justify-between rounded-md border border-slate-200 px-2.5 py-2 transition-colors duration-150 hover:bg-slate-50" data-testid={`lead-attachment-${i}`}>
                        <button onClick={() => setAttPreview(a)} className="flex min-w-0 items-center gap-2 text-left text-sm text-indigo-700 hover:underline" data-testid={`lead-attachment-view-${i}`}>
                          <FileText className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{a.name}</span>
                        </button>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-400">{a.by} · {fmtDate(a.at)}</span>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => deleteAttachment(i)} data-testid={`lead-attachment-delete-${i}`} aria-label="Remove attachment">
                            <Trash2 className="h-3 w-3 text-rose-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {(!active.attachments || active.attachments.length === 0) && <div className="text-xs text-slate-400">No files attached. Any file type can be attached — click a file to preview it here.</div>}
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <History className="h-4 w-4 text-indigo-500" /> History
                  </h3>
                  <div data-testid="lead-history">
                    {(active.history || []).slice().reverse().map((h, i) => (
                      <div key={i} className="border-l-2 border-slate-200 pb-3 pl-3">
                        <div className="text-sm text-slate-800">{h.detail}</div>
                        <div className="text-xs text-slate-400">{h.by} · {fmtDateTime(h.at)}</div>
                      </div>
                    ))}
                    {(!active.history || active.history.length === 0) && <div className="text-xs text-slate-400">No history yet.</div>}
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-900">Notes</h3>
                  <div className="space-y-2">
                    {(active.notes || []).slice().reverse().map((n, i) => (
                      <div key={i} className="rounded-md bg-slate-50 p-2.5">
                        <div className="text-sm text-slate-800">{n.text}</div>
                        <div className="mt-1 text-xs text-slate-400">{n.by} · {fmtDate(n.at)}</div>
                      </div>
                    ))}
                  </div>
                  <Textarea placeholder="Add a note..." value={noteText} onChange={(e) => setNoteText(e.target.value)} className="mt-2" data-testid="note-input" />
                  <Button size="sm" variant="outline" className="mt-2" onClick={addNote} data-testid="add-note-button">Add Note</Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
